/* ==========================================================================
   Oates Companion Products (v71)
   - Toggles .companion-option--selected on checkbox change
   - Reveals inline variant picker for the selected companion
   - Recalculates the "Add N Items to Cart — $TOTAL" label
   - Intercepts the product form submit and adds parent + all checked
     companions (with their selected variants) via /cart/add.js in one call,
     then triggers the cart drawer to refresh (or falls back to redirecting
     to /cart if no drawer is present).
   ========================================================================== */
(function () {
  'use strict';

  const form = document.getElementById('product-form');
  const group = document.querySelector('[data-companion-group]');
  const atcBtn = document.querySelector('#ProductAddToCart');
  const atcLabel = atcBtn ? atcBtn.querySelector('[data-atc-label]') : null;
  const totalLine = document.querySelector('[data-companion-total-line]');
  const qtyInput = document.getElementById('qty-value');

  if (!form || !atcBtn || !atcLabel) return;

  const baseProductPrice = parseInt(atcBtn.getAttribute('data-product-base-price') || '0', 10);
  const defaultAtcLabel = atcBtn.getAttribute('data-atc-label-default') || atcLabel.textContent.trim();

  function fmtMoney(cents) {
    // Match Shopify's default USD format ($X,XXX.XX). If the site uses
    // a non-USD currency, this still displays a sensible dollar figure and
    // the true currency-formatted totals appear in the cart drawer.
    const n = (cents / 100).toFixed(2);
    return '$' + n.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function getSelectedCompanions() {
    if (!group) return [];
    const out = [];
    group.querySelectorAll('[data-companion-item]').forEach(item => {
      const cb = item.querySelector('[data-companion-checkbox]');
      if (!cb || !cb.checked || cb.disabled) return;
      const priceEl = item.querySelector('[data-companion-price]');
      const variantSelect = item.querySelector('[data-companion-variant-select]');
      let variantId, unitPrice;
      if (variantSelect) {
        const opt = variantSelect.options[variantSelect.selectedIndex];
        variantId = opt ? opt.value : null;
        unitPrice = opt ? parseInt(opt.getAttribute('data-price') || '0', 10) : 0;
      } else {
        // Single-variant companion — we don't have the variant id in the DOM
        // for this case, so we use the handle to look it up via /products/{handle}.js
        // (handled server-side in the add flow).
        variantId = null;
        unitPrice = priceEl ? parseInt(priceEl.getAttribute('data-base-price') || '0', 10) : 0;
      }
      out.push({
        handle: item.getAttribute('data-companion-handle'),
        variantId: variantId,
        unitPrice: unitPrice,
        el: item
      });
    });
    return out;
  }

  function updateAtcLabel() {
    const parentQty = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));
    const companions = getSelectedCompanions();
    const parentSubtotal = baseProductPrice * parentQty;
    const companionSubtotal = companions.reduce((s, c) => s + c.unitPrice, 0);
    const grandTotal = parentSubtotal + companionSubtotal;

    if (companions.length === 0) {
      atcLabel.textContent = defaultAtcLabel;
      if (totalLine) { totalLine.hidden = true; totalLine.innerHTML = ''; }
      return;
    }

    const itemCount = 1 + companions.length;
    atcLabel.textContent = 'Add ' + itemCount + ' Items to Cart — ' + fmtMoney(grandTotal);
    if (totalLine) {
      totalLine.hidden = false;
      totalLine.innerHTML =
        'This item <strong>' + fmtMoney(parentSubtotal) + '</strong>' +
        ' + Add-ons <strong>' + fmtMoney(companionSubtotal) + '</strong>';
    }
  }

  // Toggle selected state + variant picker visibility
  if (group) {
    group.querySelectorAll('[data-companion-checkbox]').forEach(cb => {
      cb.addEventListener('change', () => {
        const item = cb.closest('[data-companion-item]');
        if (item) item.classList.toggle('companion-option--selected', cb.checked);
        updateAtcLabel();
      });
    });
    group.querySelectorAll('[data-companion-variant-select]').forEach(sel => {
      sel.addEventListener('change', updateAtcLabel);
      // Prevent selecting an option from bubbling up and toggling the parent checkbox
      sel.addEventListener('click', (e) => e.stopPropagation());
    });
    // Prevent clicks inside the variant picker area from toggling the checkbox
    group.querySelectorAll('[data-companion-variant-picker]').forEach(el => {
      el.addEventListener('click', (e) => e.stopPropagation());
    });
  }

  if (qtyInput) qtyInput.addEventListener('input', updateAtcLabel);
  // Also update label when the user clicks the +/- qty buttons (they mutate the input value)
  document.querySelectorAll('.quantity-selector__btn').forEach(btn => {
    btn.addEventListener('click', () => setTimeout(updateAtcLabel, 0));
  });

  // ---- Ajax add flow ----------------------------------------------------

  async function resolveVariantIdForHandle(handle) {
    // Used only for single-variant companions (no variant select in DOM).
    try {
      const res = await fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' });
      if (!res.ok) return null;
      const data = await res.json();
      const first = (data.variants || []).find(v => v.available) || (data.variants || [])[0];
      return first ? first.id : null;
    } catch (e) { return null; }
  }

  async function buildCartItems() {
    // Parent product first
    const parentIdInput = form.querySelector('input[name="id"]');
    const parentId = parentIdInput ? parentIdInput.value : null;
    const parentQty = Math.max(1, parseInt((qtyInput && qtyInput.value) || '1', 10));
    if (!parentId) return null;

    const items = [{ id: parseInt(parentId, 10), quantity: parentQty }];

    const companions = getSelectedCompanions();
    for (const c of companions) {
      let variantId = c.variantId;
      if (!variantId) {
        variantId = await resolveVariantIdForHandle(c.handle);
      }
      if (variantId) items.push({ id: parseInt(variantId, 10), quantity: 1 });
    }
    return items;
  }

  async function refreshCartDrawer() {
    const cartDrawer = document.querySelector('cart-drawer');
    if (!cartDrawer || typeof cartDrawer.renderContents !== 'function') return false;
    try {
      const res = await fetch('/?sections=cart-drawer,cart-icon-bubble', { credentials: 'same-origin' });
      if (!res.ok) return false;
      const sections = await res.json();
      // Dawn's renderContents expects a parsedState object with `sections` and `items_count`
      // We fetch /cart.js separately to get items_count.
      const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = cartRes.ok ? await cartRes.json() : { item_count: 0, items: [] };
      cartDrawer.renderContents({
        sections: sections,
        items: cart.items || [],
        item_count: cart.item_count || 0
      });
      if (typeof cartDrawer.open === 'function') cartDrawer.open();
      return true;
    } catch (e) {
      return false;
    }
  }

  form.addEventListener('submit', async function (event) {
    // Only intercept if there are checked companions. Otherwise let Shopify handle it normally.
    const companions = getSelectedCompanions();
    if (companions.length === 0) return;

    event.preventDefault();
    if (atcBtn.disabled) return;

    document.body.classList.add('companion-adding');
    atcBtn.disabled = true;
    const originalLabel = atcLabel.textContent;
    atcLabel.textContent = 'Adding…';

    try {
      const items = await buildCartItems();
      if (!items || items.length === 0) throw new Error('No items to add');

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: items })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.description || err.message || 'Unable to add to cart');
      }

      // Try to refresh the cart drawer. If no drawer, redirect to /cart.
      const drawered = await refreshCartDrawer();
      if (!drawered) {
        window.location.href = '/cart';
        return;
      }

      // Reset UI
      if (group) {
        group.querySelectorAll('[data-companion-checkbox]').forEach(cb => {
          cb.checked = false;
          const item = cb.closest('[data-companion-item]');
          if (item) item.classList.remove('companion-option--selected');
        });
      }
      updateAtcLabel();
    } catch (err) {
      console.error('[oates-companions] add-to-cart failed:', err);
      alert('Could not add all items to your cart. ' + (err.message || 'Please try again.'));
      atcLabel.textContent = originalLabel;
    } finally {
      atcBtn.disabled = false;
      document.body.classList.remove('companion-adding');
    }
  });

  // Initial label sync
  updateAtcLabel();
})();
